import { useLocation } from 'wouter';
import { trpc } from '../../lib/trpc';
import { useAsync } from '../../lib/useAsync';
import { Loading, ErrorBox, KpiCard, PageTitle } from '../../components/admin/ui';
import { MrrChart } from '../../components/admin/MrrChart';
import { formatBRLCompact, formatInt, LEAD_ESTAGIO_LABEL, PRIORIDADE_LABEL, PRIORIDADE_COLOR } from '../../lib/format';

function Bloco({ titulo, para, children }: { titulo: string; para: string; children: React.ReactNode }) {
  const [, navigate] = useLocation();
  return (
    <div className="card p-5">
      <div className="mb-3 flex items-center justify-between">
        <div className="text-[15px] font-semibold text-ink">{titulo}</div>
        <button className="text-xs font-medium text-accent hover:underline" onClick={() => navigate(para)}>
          ver tudo →
        </button>
      </div>
      {children}
    </div>
  );
}

export function AdminCockpit() {
  const [, navigate] = useLocation();
  const { data, loading, error, reload } = useAsync(() => trpc.admin.cockpit.query());

  return (
    <div className="mx-auto max-w-6xl">
      <PageTitle titulo="Visão geral" />

      {loading && <Loading />}
      {error && <ErrorBox msg={error} onRetry={reload} />}

      {data && (
        <>
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
            <button onClick={() => navigate('/admin/financeiro')} className="text-left">
              <KpiCard label="MRR" valor={formatBRLCompact(data.kpis.mrr)} destaque />
            </button>
            <button onClick={() => navigate('/admin/clientes')} className="text-left">
              <KpiCard label="Clientes ativos" valor={formatInt(data.kpis.clientesAtivos)} />
            </button>
            <button onClick={() => navigate('/admin/leads')} className="text-left">
              <KpiCard label="Leads no funil" valor={formatInt(data.kpis.leadsNoFunil)} />
            </button>
            <button onClick={() => navigate('/admin/suporte')} className="text-left">
              <KpiCard label="Tickets abertos" valor={formatInt(data.kpis.ticketsAbertos)} />
            </button>
            <button onClick={() => navigate('/admin/saude')} className="text-left">
              <KpiCard
                label="Alertas de agentes"
                valor={formatInt(data.kpis.alertasAgentes)}
                sub={data.kpis.alertasAgentes > 0 ? <span className="text-risk">requer atenção</span> : 'tudo ok'}
              />
            </button>
          </div>

          {data.mrrHistorico.length > 0 && (
            <div className="card mt-4 p-[26px]">
              <div className="text-[15px] font-semibold text-ink">MRR nos últimos meses</div>
              <div className="mt-0.5 text-xs text-ink-3">receita recorrente mensal, em R$ mil</div>
              <MrrChart historico={data.mrrHistorico} />
            </div>
          )}

          <div className="mt-4 grid gap-4 lg:grid-cols-3">
            <Bloco titulo="Últimos leads" para="/admin/leads">
              <ul className="space-y-2.5">
                {data.ultimosLeads.length === 0 && <li className="text-sm text-ink-4">Sem leads.</li>}
                {data.ultimosLeads.map((l) => (
                  <li
                    key={l.id}
                    className="flex cursor-pointer items-center justify-between text-sm hover:opacity-80"
                    onClick={() => navigate(`/admin/leads/${l.id}`)}
                  >
                    <div className="min-w-0">
                      <div className="truncate text-ink">{l.nome}</div>
                      <div className="text-xs text-ink-3">{LEAD_ESTAGIO_LABEL[l.estagio]}</div>
                    </div>
                    <span className="num shrink-0 text-ink-2">{formatBRLCompact(l.mrrPotencial)}</span>
                  </li>
                ))}
              </ul>
            </Bloco>

            <Bloco titulo="Clientes em risco" para="/admin/clientes">
              <ul className="space-y-2.5">
                {data.clientesRisco.length === 0 && <li className="text-sm text-ink-4">Nenhum cliente em risco.</li>}
                {data.clientesRisco.map((c) => (
                  <li
                    key={c.orgId}
                    className="flex cursor-pointer items-center justify-between text-sm hover:opacity-80"
                    onClick={() => navigate(`/admin/clientes/${encodeURIComponent(c.orgId)}`)}
                  >
                    <span className="truncate text-ink">{c.nome}</span>
                    <span className="shrink-0 text-xs text-risk">{c.motivo}</span>
                  </li>
                ))}
              </ul>
            </Bloco>

            <Bloco titulo="Tickets urgentes" para="/admin/suporte">
              <ul className="space-y-2.5">
                {data.ticketsUrgentes.length === 0 && <li className="text-sm text-ink-4">Nenhum ticket urgente.</li>}
                {data.ticketsUrgentes.map((t) => (
                  <li
                    key={t.id}
                    className="flex cursor-pointer items-center justify-between gap-2 text-sm hover:opacity-80"
                    onClick={() => navigate(`/admin/suporte/${t.id}`)}
                  >
                    <div className="min-w-0">
                      <div className="truncate text-ink">{t.assunto}</div>
                      <div className="text-xs text-ink-3">{t.cliente}</div>
                    </div>
                    <span className="shrink-0 text-xs font-medium" style={{ color: PRIORIDADE_COLOR[t.prioridade] }}>
                      {PRIORIDADE_LABEL[t.prioridade]}
                    </span>
                  </li>
                ))}
              </ul>
            </Bloco>
          </div>
        </>
      )}
    </div>
  );
}
